import { clsx } from 'clsx'
import { type ComponentChildren, type JSX } from 'preact'
import { type Signal } from '@preact/signals'

import { Typography } from '../typography'

interface Props
	extends Omit<
		JSX.HTMLAttributes<HTMLInputElement>,
		'endAdornment' | 'onInput' | 'size'
	> {
	className?: string
	endAdornment?: ComponentChildren
	error?: string
	fullWidth?: boolean
	hasError?: boolean
	helperText?: string
	id: string
	label?: string
	onInput: (value: string) => void
	size?: 'normal' | 'small'
	startAdornment?: ComponentChildren
	subLabel?: string
	value: Signal<string | number> | string
}

const inputClasses = {
	default:
		'border block w-full rounded border-black-500 bg-black-800 py-2.5 pl-4 pr-2.5 font-inter text-sm font-normal text-black-100 placeholder:font-light placeholder:text-black-400 sm:text-sm sm:leading-6',
	small: 'h-8',
	normal: 'h-10',
	focus:
		'focus:border-white focus:ring-white focus-visible:ring-white focus-visible:outline-none',
	error:
		'focus:border-error focus:ring-error border-error focus-visible:ring-error focus-visible:outline-none',
	disabled: 'disabled:border-input-disabled disabled:text-input-disabled'
}

const Input = ({
	autoComplete = 'off',
	className,
	disabled,
	endAdornment,
	error,
	fullWidth,
	hasError,
	helperText,
	id,
	label,
	onInput,
	startAdornment,
	value,
	subLabel,
	size = 'normal',
	...inputProps
}: Props) => (
	<Typography
		class={clsx('block', fullWidth && 'w-full')}
		color='text-black-100'
		htmlFor={id}
		text={label as string}
		variant='label3'
	>
		{subLabel && (
			<Typography
				class='ml-1'
				color='text-black-100'
				text={subLabel}
				variant='label3'
				weight='font-normal'
			/>
		)}

		<div
			class={clsx('relative', label && 'mt-2', (error || helperText) && 'mb-2')}
		>
			{startAdornment && (
				<div class='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
					{startAdornment}
				</div>
			)}

			<input
				autoComplete={autoComplete}
				class={clsx(
					inputClasses.default,
					hasError && inputClasses.error,
					!hasError && inputClasses.focus,
					disabled && inputClasses.disabled,
					endAdornment && 'pr-9',
					startAdornment && 'pl-9',
					inputClasses[size],
					className
				)}
				disabled={disabled}
				id={id}
				onInput={e => onInput(e.currentTarget.value)}
				value={value}
				{...inputProps}
			/>

			{endAdornment && (
				<div class='absolute inset-y-0 right-4 flex items-center'>
					{endAdornment}
				</div>
			)}
		</div>
		{!hasError && helperText && (
			<Typography
				color='text-black-200'
				text={helperText}
				variant='label3'
				weight='font-normal'
			/>
		)}
		{hasError && (
			<Typography
				color='text-error'
				text={error!}
				variant='label3'
				weight='font-normal'
			/>
		)}
	</Typography>
)

export { Input }
